/**
 * Shared axios client.
 *
 * The single axios instance used by every @dfq/shared/api module. It
 * wires three cross-cutting concerns in the right order:
 *
 *   1. baseURL from VITE_API_BASE_URL and envelope-friendly defaults
 *      (Accept: application/json so the platform returns JSON on 4xx / 5xx).
 *   2. A request interceptor that attaches Authorization: Bearer token
 *      when the in-memory tokenStore has an access token. Anonymous
 *      requests (public catalog, login itself) are still permitted — the
 *      header is simply omitted when no token is present.
 *   3. A response interceptor that, on a 401 whose envelope carries
 *      error_code === "TOKEN_EXPIRED" (or an equivalent signal), attempts
 *      exactly one silent refresh via /auth/refresh and retries the
 *      original request. If the refresh itself fails, the store is cleared
 *      and an auth/tokensExpired action is dispatched on the injected
 *      Redux store so the authSlice can transition to logged-out.
 *
 * The response interceptor unwraps nothing by itself — per-endpoint
 * helpers in auth.ts (and sibling files) project the envelope into typed
 * domain values. Keeping envelope unwrap out of the interceptor means
 * callers can still inspect meta / resources / authorization when they
 * need to.
 *
 * SH-AUTH-005. Builds on SH-INFRA-002's stub.
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from '@dfq/shared/auth/tokenStore';
import type { ApiEnvelope, AuthorizationPayload, SpaAuthClient } from '@dfq/shared/types/auth';

/**
 * Contract the client needs from the Redux store to fan a logout action
 * out to the authSlice when refresh fails. The auth slice exposes
 * tokensExpired (see stores/authSlice.ts) which resets user + permission
 * state to the signed-out shape. We keep the contract narrow (just
 * dispatch) so the client never reaches into the full store.
 */
export interface StoreLike {
  dispatch: (action: { type: string; payload?: unknown }) => unknown;
}

let dispatcher: StoreLike | null = null;

/**
 * Wire the shared client to a Redux store. Called once at app bootstrap
 * from apps/*\/src/main.tsx — the Console and Storefront each own a
 * store, so each passes its own instance in.
 */
export function registerAuthStore(store: StoreLike): void {
  dispatcher = store;
}

const DEFAULT_CLIENT_TYPE: SpaAuthClient = 'storefront';

let clientType: SpaAuthClient = DEFAULT_CLIENT_TYPE;

/**
 * Declare which first-party SPA this bundle is. The platform keeps one refresh
 * cookie per client, so refresh and logout must say whose to use or clear.
 * Called once at boot by `bootstrapAuth`; defaults to the storefront until then.
 */
export function configureApiClient(options: { clientType: SpaAuthClient }): void {
  clientType = options.clientType;
}

export function getApiClientType(): SpaAuthClient {
  return clientType;
}

/**
 * Test seam. Reset the module-level dispatcher reference and configured
 * client between tests. Not exported for production consumers — kept in
 * this module because it is the only place that owns them.
 */
export function __resetAuthStoreForTests(): void {
  dispatcher = null;
  clientType = DEFAULT_CLIENT_TYPE;
}

function resolveBaseUrl(): string {
  // Populated by Vite at build time (and by Vitest during tests). Direct
  // access — any optional chaining here trips Vite's transform on some
  // parser versions.
  const raw: unknown = import.meta.env.VITE_API_BASE_URL;
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.replace(/\/$/, '') + '/api/v1';
  }
  return '/api/v1';
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: resolveBaseUrl(),
  headers: {
    Accept: 'application/json',
  },
  // Required so the browser sends/stores the HttpOnly `dfq_refresh_token_*`
  // cookies on cross-origin /auth/refresh requests (§5.3.8 boot recovery).
  withCredentials: true,
});

// ----------------------------------------------------------------------------
// Request interceptor — attach Bearer token from the in-memory store.
// ----------------------------------------------------------------------------
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token !== null && config.headers) {
    config.headers.set('Authorization', 'Bearer ' + token);
  }
  // baseURL already ends in /api/v1 — a url that starts with /api/v1 will double-prefix.
  if (import.meta.env.DEV && typeof config.url === 'string' && config.url.startsWith('/api/v1')) {
    console.error(
      `[apiClient] URL "${config.url}" starts with /api/v1 — baseURL already adds that prefix. Use a bare path like "/admin/...".`,
    );
  }
  return config;
});

// ----------------------------------------------------------------------------
// Response interceptor — silent refresh + retry on 401 / TOKEN_EXPIRED.
// ----------------------------------------------------------------------------

type RetryableConfig = AxiosRequestConfig & {
  /** Marker so the same request is never refresh-retried twice. */
  _dfqRetried?: boolean;
  /** Marker for the refresh call itself so we never refresh the refresh. */
  _dfqIsRefresh?: boolean;
};

/**
 * In-flight refresh promise (module-level) so parallel 401s coalesce
 * into a single /auth/refresh round-trip instead of racing to rotate
 * the refresh token.
 */
let inFlightRefresh: Promise<AuthorizationPayload> | null = null;

export async function performRefresh(): Promise<AuthorizationPayload> {
  if (inFlightRefresh !== null) {
    return inFlightRefresh;
  }

  inFlightRefresh = (async () => {
    // The refresh endpoint accepts either a Bearer token (existing in-memory
    // refresh token) or this client's HttpOnly `dfq_refresh_token_<client>`
    // cookie (boot recovery path). When the in-memory token is absent we rely
    // on withCredentials to send the cookie automatically — no Authorization
    // override needed. `client_type` tells the platform which cookie to read.
    const refreshToken = getRefreshToken();
    const extraHeaders = refreshToken !== null
      ? { Authorization: 'Bearer ' + refreshToken }
      : {};

    const response = await apiClient.request<ApiEnvelope<Record<string, never>>>({
      method: 'POST',
      url: '/auth/refresh',
      data: { client_type: clientType },
      headers: extraHeaders,
      _dfqIsRefresh: true,
    } as RetryableConfig);

    const envelope = response.data;
    if (envelope.status !== 'success' || !envelope.authorization) {
      throw new Error('Refresh response missing authorization block');
    }

    setTokens({
      accessToken: envelope.authorization.token,
      refreshToken: envelope.authorization.refresh_token,
      expiresIn: envelope.authorization.expires_in,
    });

    return envelope.authorization;
  })();

  try {
    return await inFlightRefresh;
  } finally {
    inFlightRefresh = null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiEnvelope>) => {
    const originalConfig = error.config as RetryableConfig | undefined;
    const responseData = error.response?.data;
    const status = error.response?.status;

    const isTokenExpired =
      status === 401 && responseData?.error_code === 'TOKEN_EXPIRED';

    // Never attempt refresh on the refresh call itself, and never retry twice.
    if (
      !isTokenExpired ||
      originalConfig === undefined ||
      originalConfig._dfqRetried === true ||
      originalConfig._dfqIsRefresh === true
    ) {
      // Reconcile the "Redux says authenticated, tokenStore is empty"
      // inconsistency that can be left behind by HMR or a dev-server
      // restart: when a non-auth-flow request 401s and we had no token
      // to attach in the first place, the session is dead — notify the
      // auth slice so the guard bounces the user to /login on the next
      // render.
      const requestUrl = typeof originalConfig?.url === 'string' ? originalConfig.url : '';
      const isAuthFlow =
        requestUrl.startsWith('/auth/login') ||
        requestUrl.startsWith('/auth/register') ||
        originalConfig?._dfqIsRefresh === true;
      if (
        status === 401 &&
        !isAuthFlow &&
        dispatcher !== null &&
        getAccessToken() === null
      ) {
        dispatcher.dispatch({ type: 'auth/tokensExpired' });
      }
      return Promise.reject(error);
    }

    try {
      await performRefresh();
    } catch (refreshError) {
      // Refresh failed — surrender the session. We clear tokens and fan
      // a tokensExpired action out to the auth slice so the UI can
      // transition to signed-out and navigate to /login.
      clearTokens();
      if (dispatcher !== null) {
        dispatcher.dispatch({ type: 'auth/tokensExpired' });
      }
      return Promise.reject(refreshError);
    }

    // Retry the original request with the new access token. The request
    // interceptor will pick up the newly-stored access token from
    // tokenStore automatically — we just need to mark the config so the
    // response interceptor cannot loop if the retry also 401s.
    originalConfig._dfqRetried = true;
    return apiClient.request(originalConfig);
  },
);
